import React, { useMemo, useEffect } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
	AUTH_PREFIX_PATH,
	UNAUTHENTICATED_ENTRY,
	REDIRECT_URL_KEY,
} from "configs/AppConfig";
import { signOut } from "store/slices/authSlice";

/** Dispatch signOut once then navigate to `to` */
function SignOutAndRedirect({ to }) {
	const dispatch = useDispatch();
	useEffect(() => {
		if (dispatch) dispatch(signOut());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch]); // Added dispatch to dependency array for correctness
	return <Navigate to={to} replace />;
}

/**
 * ManagerUnauthorizedRedirect: runs redirect logic once on mount.
 * The original useEffect hook included `allowedPaths` in its dependency array.
 * This caused an infinite render loop because `allowedPaths` is a new array on every render,
 * which re-triggered the effect and the navigation.
 * By using an empty dependency array `[]`, we ensure the effect runs only *once* when the
 * component mounts, which is the correct behavior for a one-time redirect.
 */
function ManagerUnauthorizedRedirect({ allowedPaths = [], fallbackPath = "/app" }) {
	const navigate = useNavigate();

	useEffect(() => {
		if (allowedPaths && allowedPaths.length > 0) {
			navigate(allowedPaths[0], { replace: true });
			return;
		}

		try {
			// This attempts to navigate back one step in history.
			navigate(-1, { replace: true });

			// As a fallback in case navigate(-1) leads nowhere (e.g., new tab),
			// this will redirect to a safe path after a short delay.
			const t = setTimeout(() => {
				navigate(fallbackPath, { replace: true });
			}, 150);

			return () => clearTimeout(t);
		} catch (e) {
			// If navigate(-1) throws an error, go straight to the fallback.
			navigate(fallbackPath, { replace: true });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // <-- THE FIX: Empty dependency array prevents the infinite loop.

	return null; // This component's purpose is redirection, not rendering UI.
}

const ProtectedRoute = () => {
	// --- hooks first (always)
	const { token, user } = useSelector((state) => state.auth || {});
	const location = useLocation();
	const pathname = location?.pathname ?? "/";

	// Quick computed values
	const loginUrl = `${AUTH_PREFIX_PATH}${UNAUTHENTICATED_ENTRY}?${REDIRECT_URL_KEY}=${encodeURIComponent(
		pathname
	)}`;

	// Normalize permissions early (must be defined before any memo that uses it)
	const permissions = Array.isArray(user?.roles) ? user.roles : [];

	// permission map (stable)
	const routePermissionMap = useMemo(
		() => [
			{ prefix: "/app/dashboards", permission: "DASHBOARD" },
			{ prefix: "/app/managers", permission: "MANAGER" },
			{ prefix: "/app/clients", permission: "RENTER" },
			{ prefix: "/app/bailleurs", permission: "OWNER" },
			{ prefix: "/app/biens", permission: "ANNOUNCEMENT" },
			{ prefix: "/app/type-biens", permission: "BIEN" },
			{ prefix: "/app/litiges", permission: "DISPUTE" },
		],
		[]
	);

	// allowedPaths depends on permissions and the permission map
	const allowedPaths = useMemo(
		() => routePermissionMap.filter((rp) => permissions.includes(rp.permission)).map((rp) => rp.prefix),
		[routePermissionMap, permissions]
	);

	// --- guards / returns
	if (!token) {
		return <SignOutAndRedirect to={loginUrl} />;
	}

	if (user?.role?.toUpperCase() === "ADMIN") {
		return <Outlet />;
	}

	if (user?.role?.toUpperCase() === "MANAGER") {
		// allow if current path matches a permission
		for (const rp of routePermissionMap) {
			if (pathname.startsWith(rp.prefix) && permissions.includes(rp.permission)) {
				return <Outlet />;
			}
		}

		// manager not permitted -> redirect intelligently
		return <ManagerUnauthorizedRedirect allowedPaths={allowedPaths} fallbackPath="/app" />;
	}

	// other roles -> sign out & redirect
	return <SignOutAndRedirect to={loginUrl} />;
};

export default ProtectedRoute;

